import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { openPayslip } from "@/pages/rrhh/Nominas";
import { Upload, FileText, Trash2, Check, Pencil, Loader2, Sparkles, CheckCircle2 } from "lucide-react";

const euro = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }));

function EstadoBadge({ ps }) {
  if (ps.status_proc === "procesando") return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1"><Loader2 className="h-3 w-3 animate-spin" />Procesando</Badge>;
  if (ps.share_status === "compartida") return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1"><CheckCircle2 className="h-3 w-3" />Compartida</Badge>;
  return <Badge variant="secondary">Pendiente</Badge>;
}

function EditDialog({ open, onOpenChange, ps, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { if (ps) setF({ period: ps.period || "", month_label: ps.month_label || "", gross: ps.gross ?? "", coste_empresa: ps.coste_empresa ?? "", irpf: ps.irpf ?? "", ss: ps.ss ?? "", net: ps.net ?? "" }); }, [ps]);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      ["period", "month_label"].forEach((k) => { if (f[k]) payload[k] = f[k]; });
      ["gross", "coste_empresa", "irpf", "ss", "net"].forEach((k) => { if (f[k] !== "" && f[k] != null) payload[k] = Number(f[k]); });
      await api.patch(`/hr/payslips/${ps.id}`, payload);
      toast.success("Nómina actualizada");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error("Error al guardar"); } finally { setSaving(false); }
  };
  if (!ps) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Editar nómina</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Periodo (AAAA-MM)</Label><Input value={f.period || ""} onChange={(e) => setF({ ...f, period: e.target.value })} data-testid="nom-edit-period" /></div>
          <div><Label className="text-xs">Etiqueta mes</Label><Input value={f.month_label || ""} onChange={(e) => setF({ ...f, month_label: e.target.value })} /></div>
          <div><Label className="text-xs">Bruto</Label><Input type="number" step="0.01" value={f.gross} onChange={(e) => setF({ ...f, gross: e.target.value })} data-testid="nom-edit-gross" /></div>
          <div><Label className="text-xs">SS empresa</Label><Input type="number" step="0.01" value={f.coste_empresa} onChange={(e) => setF({ ...f, coste_empresa: e.target.value })} data-testid="nom-edit-coste" /></div>
          <div><Label className="text-xs">IRPF</Label><Input type="number" step="0.01" value={f.irpf} onChange={(e) => setF({ ...f, irpf: e.target.value })} /></div>
          <div><Label className="text-xs">SS trabajador</Label><Input type="number" step="0.01" value={f.ss} onChange={(e) => setF({ ...f, ss: e.target.value })} /></div>
          <div><Label className="text-xs">Neto</Label><Input type="number" step="0.01" value={f.net} onChange={(e) => setF({ ...f, net: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="nom-edit-save">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GestionNominas() {
  const { data: payslips, loading, refetch } = useFetch("/hr/payslips");
  const { data: profiles } = useFetch("/hr/profiles");
  const { data: salaryLines, refetch: refetchLines } = useFetch("/hr/salary-lines");
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("equipo");
  const fileRef = useRef(null);

  // Poll mientras haya nóminas procesándose
  const processing = (payslips || []).some((p) => p.status_proc === "procesando");
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(refetch, 4000);
    return () => clearInterval(t);
  }, [processing, refetch]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await api.post("/hr/payslips/bulk", fd);
      toast.success(`${res.data.queued} documento(s) en proceso. La IA detecta TODAS las nóminas que contengan (aunque haya varias por página) y las asigna por DNI/nombre.`);
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const assign = async (pid, user_id) => {
    if (!user_id) return;
    try { await api.post(`/hr/payslips/${pid}/asignar`, { user_id }); toast.success("Asignada al trabajador"); refetch(); }
    catch (e) { toast.error("Error al asignar"); }
  };
  const validar = async (pid) => {
    try { await api.post(`/hr/payslips/${pid}/validar`); toast.success("Validada · el empleado ya puede verla"); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error al validar"); }
  };
  const del = async (pid) => {
    if (!window.confirm("¿Eliminar esta nómina?")) return;
    try { await api.delete(`/hr/payslips/${pid}`); toast.success("Eliminada"); refetch(); }
    catch (e) { toast.error("Error"); }
  };
  const setLine = async (pid, target) => {
    try {
      await api.post(`/hr/payslips/${pid}/salary-line`, { target });
      toast.success(target === "__ignore__" ? "Nómina ignorada (no se imputa)" : "Imputación actualizada en Gastos Fijos");
      refetch(); refetchLines();
    } catch (e) { toast.error("No se pudo actualizar la imputación"); }
  };

  const rows = payslips || [];
  const equipoRows = rows.filter((p) => !p.salary_external);
  const sinRows = rows.filter((p) => p.salary_external);
  const shown = tab === "equipo" ? equipoRows : sinRows;
  const sinAsignar = equipoRows.filter((p) => !p.user_id).length;
  const pendientes = equipoRows.filter((p) => p.user_id && p.share_status !== "compartida" && p.status_proc !== "procesando").length;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="nominas-page">
      <PageHeader title="Nóminas" subtitle="Sube las nóminas (PDF sueltos o un ZIP). La IA extrae bruto, IRPF, SS y coste de empresa, y las asigna a cada trabajador. Al validarlas, el empleado ve la suya y el coste alimenta Gastos Fijos → Salarios." />

      <Card className="border-border shadow-none p-5">
        <div className="rounded-lg border-2 border-dashed border-border p-6 flex flex-col items-center justify-center gap-3 bg-muted/20 text-center">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Sparkles className="h-4 w-4 text-gold" />
            {uploading ? "Subiendo…" : "Arrastra o selecciona los PDF (una nómina o un PDF con TODAS las del mes), o un ZIP"}
          </div>
          <input ref={fileRef} type="file" accept="application/pdf,.zip,application/zip" multiple className="hidden" onChange={onUpload} data-testid="nom-upload-input" />
          <Button className="gap-1.5" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="nom-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Subir nóminas
          </Button>
          <div className="flex gap-4 text-xs text-muted-foreground pt-1">
            {sinAsignar > 0 && <span className="text-amber-600" data-testid="nom-stat-sinasignar">⚠ {sinAsignar} sin asignar</span>}
            {pendientes > 0 && <span data-testid="nom-stat-pendientes">{pendientes} pendientes de validar</span>}
          </div>
        </div>
      </Card>

      <Card className="border-border shadow-none p-0 overflow-hidden">
        <div className="flex items-center gap-1 border-b border-border bg-muted/30 px-3 pt-3">
          <button
            onClick={() => setTab("equipo")}
            className={`rounded-t-md px-4 py-2 text-sm font-medium transition-colors ${tab === "equipo" ? "bg-background border border-b-0 border-border text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="nom-tab-equipo">
            Equipo <span className="ml-1 text-xs text-muted-foreground">({equipoRows.length})</span>
          </button>
          <button
            onClick={() => setTab("sin")}
            className={`rounded-t-md px-4 py-2 text-sm font-medium transition-colors ${tab === "sin" ? "bg-background border border-b-0 border-border text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="nom-tab-sin">
            Sin perfil <span className="ml-1 text-xs text-muted-foreground">({sinRows.length})</span>
          </button>
        </div>
        <Table data-testid="nom-table">
          <TableHeader>
            <TableRow>
              <TableHead>Trabajador</TableHead>
              <TableHead>Periodo</TableHead>
              <TableHead className="text-right">Bruto</TableHead>
              <TableHead className="text-right">SS empresa</TableHead>
              <TableHead className="text-right">Coste total</TableHead>
              <TableHead className="text-right">IRPF</TableHead>
              <TableHead className="text-right">Neto</TableHead>
              <TableHead>Imputación (Gasto Fijo)</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Cargando…</TableCell></TableRow>
            ) : shown.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground" data-testid="nom-empty">{tab === "equipo" ? "Sin nóminas del equipo todavía. Sube tus PDF o un ZIP arriba." : "No hay nóminas sin perfil de plataforma."}</TableCell></TableRow>
            ) : shown.map((p) => {
              const coste = (p.gross || 0) + (p.coste_empresa || 0);
              return (
                <TableRow key={p.id} data-testid={`nom-row-${p.id}`}>
                  <TableCell className="min-w-[190px]">
                    {p.user_id ? (
                      <span className="font-medium text-sm">{p.user_name || p.nombre || "—"}</span>
                    ) : (
                      <select
                        className="border border-amber-300 bg-amber-50 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                        defaultValue=""
                        onChange={(e) => assign(p.id, e.target.value)}
                        data-testid={`nom-assign-${p.id}`}
                      >
                        <option value="" disabled>Asignar a…{p.nombre ? ` (${p.nombre})` : ""}</option>
                        {(profiles || []).map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                      </select>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{p.month_label || p.period || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{euro(p.gross)}</TableCell>
                  <TableCell className="text-right tabular-nums">{euro(p.coste_empresa)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{p.gross != null ? euro(coste) : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{euro(p.irpf)}</TableCell>
                  <TableCell className="text-right tabular-nums">{euro(p.net)}</TableCell>
                  <TableCell className="min-w-[210px]">
                    {p.status_proc === "procesando" ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <select
                        className={`w-full max-w-[210px] rounded-md border px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary ${p.salary_ignored ? "border-border bg-muted text-muted-foreground" : "border-border bg-background"}`}
                        value={p.salary_ignored ? "__ignore__" : (p.salary_line_id || "")}
                        onChange={(e) => setLine(p.id, e.target.value)}
                        data-testid={`nom-line-${p.id}`}
                        title="Línea de Gasto Fijo (Salariales) a la que se imputa esta nómina"
                      >
                        {!p.salary_line_id && !p.salary_ignored && <option value="" disabled>Sin imputar…</option>}
                        <option value="__auto__">⟳ Automático (por nombre)</option>
                        <optgroup label="Líneas de salario">
                          {(salaryLines || []).map((l) => <option key={l.id} value={l.id}>{l.name}{l.is_user ? " · 👤" : ""}</option>)}
                        </optgroup>
                        <option value="__external__">➕ Crear línea externa{p.nombre ? ` (${p.nombre})` : ""}</option>
                        <option value="__ignore__">🚫 Ignorar (no imputar)</option>
                      </select>
                    )}
                  </TableCell>
                  <TableCell><EstadoBadge ps={p} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      {p.user_id && p.share_status !== "compartida" && p.status_proc !== "procesando" && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" title="Validar (compartir con el empleado)" onClick={() => validar(p.id)} data-testid={`nom-validate-${p.id}`}><Check className="h-4 w-4" /></Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Ver PDF" onClick={() => openPayslip(p.id)} data-testid={`nom-view-${p.id}`}><FileText className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Editar importes" onClick={() => setEditing(p)} data-testid={`nom-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" title="Eliminar" onClick={() => del(p.id)} data-testid={`nom-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <EditDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} ps={editing} onSaved={refetch} />
    </div>
  );
}
